@file:Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")

package com.wintermar.inspection.core.ui.component.image.util

import kotlinx.browser.document
import kotlinx.browser.window
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLAnchorElement
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

actual object FileDownloader {
    actual fun download(file: FileModel, fileMimeType: FileMimeType) {
        val urlPrefix = "http"
        try {
            val fileName = file.originalName
            val url = when {
                file.path.isNotEmpty() && file.path.startsWith(urlPrefix) -> file.path
                file.platformFile != null -> {
                    val nativeBrowserFile: org.w3c.files.File = file.platformFile.file
                    org.w3c.dom.url.URL.createObjectURL(nativeBrowserFile)
                }

                file.blob is ByteArray -> {
                    val uint8 = Uint8Array((file.blob).toTypedArray())
                    val blob = Blob(arrayOf(uint8), BlobPropertyBag(type = fileMimeType.value))
                    org.w3c.dom.url.URL.createObjectURL(blob)
                }

                else -> return
            }

            // Handle embedded contexts (iframes) by using multiple fallback strategies
            if (isCrossOriginUrl(url)) {
                // For cross-origin URLs, use a fetch + blob approach
                downloadCrossOrigin(url, fileName)
            } else {
                // For same-origin and blob URLs, use the standard approach
                downloadStandard(url, fileName)
            }
        } catch (e: Exception) {
            window.alert("Download failed: ${e.message}")
        }
    }

    private fun downloadStandard(url: String, fileName: String) {
        try {
            val anchor = document.createElement("a") as HTMLAnchorElement
            anchor.href = url
            anchor.download = fileName
            anchor.style.display = "none"
            document.body?.appendChild(anchor)
            anchor.click()
            document.body?.removeChild(anchor)
            window.alert("$fileName saved successfully")
        } catch (@Suppress("UNUSED_PARAMETER") e: Exception) {
            // Fallback if standard method fails
            downloadViaWindow(url, fileName)
        }
    }

    private fun downloadCrossOrigin(url: String, fileName: String) {
        try {
            // Try to fetch and create blob for download
            val promise = js("fetch(url)")
            promise.then({ response: dynamic -> response.blob() })
                .then({ blob: dynamic ->
                    val blobUrl = org.w3c.dom.url.URL.createObjectURL(blob as Blob)
                    downloadStandard(blobUrl, fileName)
                    // Clean up blob URL
                    org.w3c.dom.url.URL.revokeObjectURL(blobUrl)
                })
                .catch({ _: dynamic ->
                    downloadViaWindow(url, fileName)
                })
        } catch (@Suppress("UNUSED_PARAMETER") e: Exception) {
            downloadViaWindow(url, fileName)
        }
    }

    private fun downloadViaWindow(url: String, fileName: String) {
        try {
            // Last resort: use window.open
            val newTab = window.open(url, "_blank")
            newTab?.focus()
            window.alert("$fileName opened in new tab")
        } catch (@Suppress("UNUSED_PARAMETER") e: Exception) {
            window.alert("Download failed")
        }
    }

    private fun isCrossOriginUrl(url: String): Boolean {
        if (url.startsWith("blob:") || url.startsWith("data:")) {
            return false
        }

        return try {
            js("new URL(url)")
            val currentOrigin = js("window.location.origin")
            val urlOrigin = js("new URL(url).origin")
            currentOrigin != urlOrigin
        } catch (@Suppress("UNUSED_PARAMETER") e: Exception) {
            false
        }
    }
}
